import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { getToken, getUser } from './utils/authUtil';
import ZapLoader from './components/ui/ZapLoader';

// Admin pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import StudentProfile from './pages/StudentProfile';
import AddStudent from './pages/AddStudent';
import Batches from './pages/Batches';
import QuestionBank from './pages/QuestionBank';
import Tests from './pages/Tests';
import TestCreateWizard from './pages/TestCreateWizard';
import TestPlanner from './pages/admin/TestPlanner';
import TestDetail from './pages/TestDetail';
import AdminAnalytics from './pages/admin/Analytics';
import BatchesOverview from './pages/admin/BatchesOverview';
import BatchAnalytics from './pages/admin/BatchAnalytics';
import StudentIntelligence from './pages/admin/StudentIntelligence';
import Interventions from './pages/admin/Interventions';
import Announcements from './pages/admin/Announcements';

import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminQuestionBank from './pages/SuperAdminQuestionBank';
import EditStudent from './pages/EditStudent';
import TopicsManager from './pages/TopicsManager';

// Student pages
import StudentPortal from './pages/StudentPortal';

import StudentTests from './pages/StudentTests';
import StudentPractice from './pages/StudentPractice';
import StudentAnalytics from './pages/StudentAnalytics';
import StudentProfilePage from './pages/StudentProfilePage';
import TakeTest from './pages/TakeTest';
import MockTestSetup from './pages/MockTest';
import MockTestTake from './pages/MockTestTake';
import MistakeBank from './pages/MistakeBank';
import PlacementHub from './pages/PlacementHub';
import Notifications from './pages/Notifications';
import Feedback from './pages/Feedback';
import StudentSettings from './pages/StudentSettings';
import FeedbackInbox from './pages/admin/FeedbackInbox';
import TestReview from './pages/TestReview';
import DiagnosticAnalysis from './pages/DiagnosticAnalysis';
import DiagnosticReport from './pages/DiagnosticReport';


import Landing from './pages/Landing';
import AdminShell from './components/admin/AdminShell';
import AdminSettings from './pages/admin/AdminSettings';
import PlatformUpdates from './pages/admin/PlatformUpdates';
import SuperAdminUpdates from './pages/admin/SuperAdminUpdates';
import QuestionReview from './pages/admin/QuestionReview';
import Leads from './pages/admin/Leads';
import AdminLeaderboard from './pages/admin/Leaderboard';
import StudentShell from './components/student/StudentShell';
import { Outlet } from 'react-router-dom';
import { PopupProvider } from './context/PopupContext';
import GlobalPopup from './components/GlobalPopup';
import ScrollToTop from './components/ScrollToTop';

// Which nav a shared /admin/* page renders: a super admin keeps the platform nav (never the college one).
const shellRole = () => { const u = getUser(); return (u?.realRole || u?.role) === 'super_admin' ? 'super_admin' : 'admin'; };
// College-operations pages a super admin does not have (the platform portal is not a college workspace).
const CollegeOnly = ({ children, to = "/super-admin" }) => (shellRole() === 'super_admin' ? <Navigate to={to} replace /> : children);

const StudentShellRoute = () => <StudentShell><Outlet /></StudentShell>;

const PrivateRoute = ({ children, allowedRole }) => {
  const token = getToken();
  const user = getUser();
  
  if (!token) {
    return <Navigate to="/login" />;
  }
  
  if (allowedRole) {
    const roles = Array.isArray(allowedRole) ? allowedRole : [allowedRole];
    const currentRole = user?.realRole || user?.role;

    // Always allow super admins
    if (currentRole === 'super_admin') {
      return children;
    }

    // Check for role match or mapped admin roles
    const isAllowed = roles.includes(currentRole) || 
                     (roles.includes('admin') && (currentRole === 'college_admin' || currentRole === 'admin'));

    if (!isAllowed) {
      return <Navigate to="/login" />;
    }
  }
  return children;
};

// Shown once per browser tab session, on genuine app bootstrap only — not on
// every client-side navigation or data refetch. The Router tree mounts
// immediately underneath it (position:fixed overlay), so pages start
// fetching their own data while the loader is still visible instead of
// waiting for it — the loader is a brand moment, not a blocking gate.
const BOOT_FLAG = 'zaptude_booted';

function App() {
  const [showBootLoader, setShowBootLoader] = useState(() => !sessionStorage.getItem(BOOT_FLAG));
  const [bootReady, setBootReady] = useState(false);

  useEffect(() => {
    if (!showBootLoader) return;
    // Nothing further to await at the app shell level (each page fetches
    // its own data) — mark ready on the next tick so ZapLoader's own
    // min/max timing (~0.8–1.5s) governs how long the brand moment shows.
    const t = setTimeout(() => setBootReady(true), 0);
    return () => clearTimeout(t);
  }, [showBootLoader]);

  return (
    <PopupProvider>
      <GlobalPopup />
      {showBootLoader && (
        <ZapLoader ready={bootReady} onDone={() => { sessionStorage.setItem(BOOT_FLAG, '1'); setShowBootLoader(false); }} />
      )}
      <Router>
      <ScrollToTop />
      <Routes>

        {/* Public */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />

        {/* Admin routes */}
        <Route path="/admin/dashboard"
          element={
            <PrivateRoute allowedRole="admin">
              <AdminShell role={shellRole()}><Dashboard /></AdminShell>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/students"
          element={
            <PrivateRoute allowedRole="admin">
              <AdminShell role={shellRole()}><Students /></AdminShell>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/students/add"
          element={
            <PrivateRoute allowedRole="admin">
              <AdminShell role={shellRole()}><AddStudent /></AdminShell>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/students/:id"
          element={
            <PrivateRoute allowedRole="admin">
              <AdminShell role={shellRole()}><StudentProfile /></AdminShell>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/students/edit/:id"
          element={
            <PrivateRoute allowedRole="admin">
              <AdminShell role={shellRole()}><EditStudent /></AdminShell>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/batches"
          element={
            <PrivateRoute allowedRole="admin">
              <AdminShell role={shellRole()}><Batches /></AdminShell>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/questions"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <AdminShell role={shellRole()}><QuestionBank /></AdminShell>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/topics"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <AdminShell role={shellRole()}><TopicsManager /></AdminShell>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/tests"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <CollegeOnly><AdminShell role={shellRole()}><Tests /></AdminShell></CollegeOnly>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/tests/create"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <CollegeOnly><AdminShell role={shellRole()}><TestCreateWizard /></AdminShell></CollegeOnly>
            </PrivateRoute>
          }
        />
        <Route path="/admin/tests/planner"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <CollegeOnly><AdminShell role={shellRole()}><TestPlanner /></AdminShell></CollegeOnly>
            </PrivateRoute>
          }
        />
        <Route path="/admin/tests/edit/:id"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <CollegeOnly><AdminShell role={shellRole()}><TestCreateWizard /></AdminShell></CollegeOnly>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/tests/:id"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <CollegeOnly><AdminShell role={shellRole()}><TestDetail /></AdminShell></CollegeOnly>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/analytics"
          element={
            <PrivateRoute allowedRole="admin">
              <CollegeOnly><AdminShell role={shellRole()}><AdminAnalytics /></AdminShell></CollegeOnly>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/batches-overview"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <AdminShell role={shellRole()}><BatchesOverview /></AdminShell>
            </PrivateRoute>
          }
        />
        <Route path="/admin/batches/:id/analytics"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <AdminShell role={shellRole()}><BatchAnalytics /></AdminShell>
            </PrivateRoute>
          }
        />
        <Route path="/admin/students/:id/intelligence"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <AdminShell role={shellRole()}><StudentIntelligence /></AdminShell>
            </PrivateRoute>
          }
        />
        <Route path="/admin/interventions"
          element={
            <PrivateRoute allowedRole="admin">
              <CollegeOnly><AdminShell role={shellRole()}><Interventions /></AdminShell></CollegeOnly>
            </PrivateRoute>
          }
        />
        <Route path="/admin/announcements"
          element={
            <PrivateRoute allowedRole="admin">
              <CollegeOnly><AdminShell role={shellRole()}><Announcements /></AdminShell></CollegeOnly>
            </PrivateRoute>
          }
        />
        <Route path="/admin/diagnostic-analysis"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <CollegeOnly><AdminShell role={shellRole()}><DiagnosticAnalysis /></AdminShell></CollegeOnly>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/diagnostic/report/:attemptId"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <CollegeOnly><AdminShell role={shellRole()}><DiagnosticReport /></AdminShell></CollegeOnly>
            </PrivateRoute>
          } 
        />

        <Route path="/super-admin"
          element={
            <PrivateRoute allowedRole="super_admin">
              <AdminShell role="super_admin"><SuperAdminDashboard /></AdminShell>
            </PrivateRoute>
          } 
        />
        <Route path="/super-admin/questions"
          element={
            <PrivateRoute allowedRole="super_admin">
              <AdminShell role="super_admin"><SuperAdminQuestionBank /></AdminShell>
            </PrivateRoute>
          } 
        />
        <Route path="/super-admin/topics"
          element={
            <PrivateRoute allowedRole="super_admin">
              <AdminShell role="super_admin"><TopicsManager /></AdminShell>
            </PrivateRoute>
          } 
        />

        <Route path="/admin/feedback" element={<PrivateRoute allowedRole={['admin', 'super_admin']}><CollegeOnly to="/super-admin/feedback"><AdminShell role="admin"><Feedback /></AdminShell></CollegeOnly></PrivateRoute>} />
        <Route path="/admin/platform-updates" element={<PrivateRoute allowedRole="admin"><CollegeOnly><AdminShell role="admin"><PlatformUpdates /></AdminShell></CollegeOnly></PrivateRoute>} />
        <Route path="/super-admin/review" element={<PrivateRoute allowedRole="super_admin"><AdminShell role="super_admin"><QuestionReview /></AdminShell></PrivateRoute>} />
        <Route path="/super-admin/leads" element={<PrivateRoute allowedRole="super_admin"><AdminShell role="super_admin"><Leads /></AdminShell></PrivateRoute>} />
        <Route path="/super-admin/updates" element={<PrivateRoute allowedRole="super_admin"><AdminShell role="super_admin"><SuperAdminUpdates /></AdminShell></PrivateRoute>} />
        <Route path="/super-admin/feedback" element={<PrivateRoute allowedRole="super_admin"><AdminShell role="super_admin"><FeedbackInbox /></AdminShell></PrivateRoute>} />

        {/* College-scoped leaderboard (server/controllers/leaderboardController.js) — an admin always sees
            their own college; a super admin picks one college at a time. Never a merged cross-college view. */}
        <Route path="/admin/leaderboard" element={<PrivateRoute allowedRole="admin"><AdminShell role={shellRole()}><AdminLeaderboard /></AdminShell></PrivateRoute>} />
        <Route path="/super-admin/leaderboard" element={<PrivateRoute allowedRole="super_admin"><AdminShell role="super_admin"><AdminLeaderboard isSuper /></AdminShell></PrivateRoute>} />

        {/* Shared account settings (change password) for admin + super admin — the same Sheet-driven
            "Settings" row the student profile sheet already links to, scaled down to what admin has. */}
        <Route path="/admin/settings" element={<PrivateRoute allowedRole={['admin', 'super_admin']}><AdminShell role={shellRole()}><AdminSettings /></AdminShell></PrivateRoute>} />

        {/* Student routes — ONE persistent shell (app bar + bottom nav + profile sheet); the player profile and
            unread count are fetched once by the shell, not on every navigation. Full-screen exams sit outside it. */}
        <Route element={<PrivateRoute allowedRole="student"><StudentShellRoute /></PrivateRoute>}>
          <Route path="/student/dashboard" element={<StudentPortal />} />
          <Route path="/student/tests" element={<StudentTests />} />
          <Route path="/student/analytics" element={<StudentAnalytics />} />
          <Route path="/student/practice" element={<StudentPractice />} />
          <Route path="/student/profile" element={<StudentProfilePage />} />
          <Route path="/student/notifications" element={<Notifications />} />
          <Route path="/student/feedback" element={<Feedback />} />
          <Route path="/student/settings" element={<StudentSettings />} />
          <Route path="/student/test/:id/review" element={<TestReview mode="official" />} />
          <Route path="/student/mock" element={<MockTestSetup />} />
          <Route path="/student/mock/review/:id" element={<TestReview mode="practice" />} />
          <Route path="/student/mistakes" element={<MistakeBank />} />
          <Route path="/student/placement" element={<PlacementHub />} />
        </Route>

        {/* Full-screen assessment surfaces (own header, timer, anti-cheat) */}
        <Route path="/student/test/:id" element={<PrivateRoute allowedRole="student"><TakeTest /></PrivateRoute>} />
        <Route path="/student/mock/test" element={<PrivateRoute allowedRole="student"><MockTestTake /></PrivateRoute>} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" />} />

      </Routes>
    </Router>
    </PopupProvider>
  );
}

export default App;